import { createFutureOrder } from '@sp-firebase/futureOrderFB';
import { IClientExposure, IFutureOrder, IOrderDetails, ISupplierExposure } from '@sp-types/FutureOrderType';
import { NavigateFunction } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Material } from '../../../sp-types';
import { Product } from '../../../sp-types/productType';
import { MonthYearValue, sumOfMonthYearArrayValues } from '../../../utils/MonthYear/MonthYearValue';
import { accumulateExposures, calcSupplierMinusClient } from './CreateOrderSummary/OrderSummaryUtils';
import { getDeliveryDates } from './FillOrderDetails/FillOrderDetailsUtils';
import {
  adjustExposureCalendar,
  calculateMaterialsClientPayment,
} from './FOIndexedClientExposure/FillOrderAdjustmentsUtils';
import {
  AdjustmentMethodKey,
  BackToBackAdjustment,
  mapProductToSupplierExposure,
} from './RawMaterialsExposure/RawMaterialsUtils';
import { addNotification } from '../../../sp-firebase/notificationsFB';
import { Timestamp } from 'firebase/firestore';
import { triggerExposureCalc } from '../../../hooks/useExposure';
import { sortByDate } from '../../BalanceFlow/balanceUtils';
import { StatusType } from '../../../sp-types/statusType';
import { MonthYear, sortMonthYear } from '../../../utils/MonthYear/monthYear';

export const ADD_FUTURE_ORDER_ROOT_ROUTE = 'add-future-order';
export const ADD_FUTURE_ORDER_DEFAULT_ORDER_ID = 'new-order';
const MODIFIED_ORDER_SUFFIX = '_modified';

export const updateSupplierExposureByLeadTime = (
  currentSupplierExposure: ISupplierExposure[],
  materialId: string,
  newLeadTime: number,
  productDeliveries: MonthYearValue[],
): ISupplierExposure[] => {
  return currentSupplierExposure.map((item) =>
    item.materialId === materialId
      ? {
          ...item,
          supplyLeadTime: newLeadTime,
          exposureCalendar: BackToBackAdjustment(-newLeadTime, item.weightInProduct, productDeliveries),
        }
      : item,
  );
};

const cleanUUID = (uuid?: string) =>
  uuid ? (uuid.endsWith(MODIFIED_ORDER_SUFFIX) ? uuid.slice(0, -MODIFIED_ORDER_SUFFIX.length) : uuid) : undefined;

export const uploadNewFutureOrder = async (
  futureOrder: IFutureOrder,
  accountId: string,
  futureOrderStatus: StatusType = 'completed',
): Promise<boolean> => {
  const updatedFutureOrder: IFutureOrder = {
    ...futureOrder,
    status: futureOrderStatus,
    uuid: cleanUUID(futureOrder.uuid),
  };
  try {
    if (accountId) {
      await createFutureOrder(accountId, { ...updatedFutureOrder, status: futureOrderStatus });

      return true;
    } else {
      toast.error('Try Again - Account not found');
      return false;
    }
  } catch (error) {
    console.error('Error uploading future order:', error);
    return false;
  }
};

export const isFutureOrderFilledCorrectly = (futureOrder: IFutureOrder): boolean => {
  const { orderDetails, orderId, productId, futureOrderType, totalExposure } = futureOrder;
  return (
    !isFutureOrderOrderDetailsFilled(orderDetails) ||
    !orderId ||
    !productId ||
    !futureOrderType ||
    !totalExposure?.length
  );
};

const isFutureOrderOrderDetailsFilled = (orderDetails: IOrderDetails): boolean => {
  const { deliveryDates, deliveryType, productDeliveryCalendar, quantity, pricePerUnit } = orderDetails;
  return !(
    !pricePerUnit ||
    !deliveryType ||
    !quantity ||
    !productDeliveryCalendar ||
    !productDeliveryCalendar.length ||
    !deliveryDates?.length
  );
};

const indexedPages: FutureOrderPagesType[] = [
  'select-contract-type',
  'create-customer-order',
  'fill-order-details',
  'indexed-client-exposure',
  'indexed-supplier-exposure',
  'indexed-order-summary',
];

const backToBackPages: FutureOrderPagesType[] = [
  'select-contract-type',
  'create-customer-order',
  'fill-order-details',
  'back-to-back-material-breakdown',
  'back-to-back-summary',
];

export const futureOrderTypeDictionary: Record<string, { displayName: string; pageOrder: FutureOrderPagesType[] }> = {
  BackToBack: { displayName: 'Fixed', pageOrder: backToBackPages },
  Indexed: { displayName: 'Indexed', pageOrder: indexedPages },
};

export const ADD_FO_FIRST_STEP = backToBackPages[0];

export const goToNextPage = (
  currentPageId: FutureOrderPagesType,
  orderType: string,
  navigate: NavigateFunction,
  orderId: string = ADD_FUTURE_ORDER_DEFAULT_ORDER_ID,
) => {
  const pagesArray: FutureOrderPagesType[] = futureOrderTypeDictionary[orderType].pageOrder || backToBackPages;
  const currentIndex = pagesArray.indexOf(currentPageId);
  if (currentIndex >= 0 && currentIndex < pagesArray.length - 1) {
    navigate(`/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${orderId}/${pagesArray[currentIndex + 1]}`);
  }
};

export const goToPrevPage = (
  currentPageId: FutureOrderPagesType,
  orderType: string,
  navigate: NavigateFunction,
  orderId: string = ADD_FUTURE_ORDER_DEFAULT_ORDER_ID,
) => {
  const pagesArray: FutureOrderPagesType[] = futureOrderTypeDictionary[orderType].pageOrder || backToBackPages;
  const currentIndex = pagesArray.indexOf(currentPageId);
  if (currentIndex > 0) {
    navigate(`/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${orderId}/${pagesArray[currentIndex - 1]}`);
  }
};

export const goToSummaryPage = (
  orderType: string,
  navigate: NavigateFunction,
  orderId: string = ADD_FUTURE_ORDER_DEFAULT_ORDER_ID,
) => {
  const pagesArray: FutureOrderPagesType[] = futureOrderTypeDictionary[orderType].pageOrder || backToBackPages;
  navigate(`/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${orderId}/${pagesArray[pagesArray.length - 1]}`);
};

export const goToFormStart = (navigate: NavigateFunction, orderId: string = ADD_FUTURE_ORDER_DEFAULT_ORDER_ID) => {
  navigate(`/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${orderId}/fill-order-details`);
};

export const goToCompleteScreen = (navigate: NavigateFunction, orderId: string = ADD_FUTURE_ORDER_DEFAULT_ORDER_ID) => {
  navigate(`/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${orderId}/order-completed`);
};

export type FutureOrderPagesType =
  | 'select-contract-type'
  | 'create-customer-order'
  | 'fill-order-details'
  | 'indexed-client-exposure'
  | 'indexed-supplier-exposure'
  | 'indexed-order-summary'
  | 'order-summary'
  | 'order-completed'
  | 'back-to-back-summary'
  | 'back-to-back-material-breakdown';

// Need to make it work better with inputted properties
export const updateFutureOrderValueCalendars = (
  currentFutureOrder: IFutureOrder,
  products: Product[],
  materialsMap: Record<string, Material>,
  adjustmentType: AdjustmentMethodKey = 'Spot',
  indexOffset: number = 0,
): {
  productDeliveryCalendar: MonthYearValue[];
  supplierExposure: ISupplierExposure[];
  clientExposure: Record<string, IClientExposure>;
  totalExposure: { materialId: string; exposureCalendar: MonthYearValue[] }[];
  monthExposure: Record<string, MonthYearValue[]>;
} => {
  const { orderDetails } = currentFutureOrder;
  const { quantity, deliveryType, deliveryDates, productDeliveryCalendar } = orderDetails;

  const productCalendar =
    productDeliveryCalendar && sumOfMonthYearArrayValues(productDeliveryCalendar) === orderDetails.quantity
      ? productDeliveryCalendar
      : getDeliveryDates(quantity, deliveryDates || [], deliveryType, productDeliveryCalendar || []);

  const updatedSupplierExposure = calculateSupplierExposure(
    products,
    currentFutureOrder,
    materialsMap,
    productCalendar,
  );

  const updatedClientExposure = calculateClientExposure(
    currentFutureOrder,
    productCalendar,
    materialsMap,
    adjustmentType,
    products,
    indexOffset,
  );

  return {
    productDeliveryCalendar: productCalendar,
    supplierExposure: updatedSupplierExposure,
    clientExposure: updatedClientExposure,
    ...calculateTotalExposure(updatedSupplierExposure, updatedClientExposure),
  };
};
const getMonthDiff = (date1: Date, date2: Date): number =>
  (date1.getFullYear() - date2.getFullYear()) * 12 + date1.getMonth() - date2.getMonth();

const fillExposureFromToday = (exposureCalendar: MonthYearValue[]): MonthYearValue[] => {
  const firstExposure = exposureCalendar?.sort(sortByDate)[0];

  const currDate = new Date();

  const monthDiff = getMonthDiff(
    new Date(firstExposure.date.year, firstExposure.date.month - 1, 1),
    new Date(currDate.getFullYear(), currDate.getMonth(), 1),
  );
  const replacer =
    monthDiff > 0
      ? { value: firstExposure.value, date: { month: currDate.getMonth() + 1, year: currDate.getFullYear() } }
      : null;

  return exposureCalendar.map((val) =>
    replacer && val.date.year === firstExposure.date.year && val.date.month === firstExposure.date.month
      ? replacer
      : val,
  );
};

export const getLastDate = (productDelivery: MonthYearValue[]): MonthYear =>
  productDelivery?.sort((a: MonthYearValue, b: MonthYearValue) => sortMonthYear(b.date, a.date))[0]?.date;

const calculateSupplierExposure = (
  products: Product[],
  currentFutureOrder: IFutureOrder,
  materialsMap: Record<string, Material>,
  productDeliveryCalendar: MonthYearValue[],
): ISupplierExposure[] => {
  const newMapProductToSupplierExposure = mapProductToSupplierExposure(products, currentFutureOrder, materialsMap);

  return newMapProductToSupplierExposure.map((material) => {
    const origExposureCalendar = adjustExposureCalendar(
      productDeliveryCalendar || [],
      material.adjustment,
      -material.supplyLeadTime,
      material.weightInProduct,
    );

    // const exposureCalendar = fillExposureFromToday(origExposureCalendar);

    return { ...material, exposureCalendar: origExposureCalendar };
  });
};

const calculateClientExposure = (
  currentFutureOrder: IFutureOrder,
  productDeliveryCalendar: MonthYearValue[],
  materialsMap: Record<string, Material>,
  defaultAdjustmentType: string,
  products: Product[],
  defaultLeadTime: number,
): Record<string, IClientExposure> => {
  const { clientExposure } = currentFutureOrder;
  const firstMaterial = Object.values(clientExposure)[0];
  return calculateMaterialsClientPayment(
    currentFutureOrder,
    productDeliveryCalendar,
    currentFutureOrder.clientExposure,
    firstMaterial?.adjustment || defaultAdjustmentType,
    firstMaterial?.indexOffset || defaultLeadTime,
    materialsMap,
    products.find(({ uuid }) => uuid === currentFutureOrder.productId),
  );
};

// The calculation functions of total exposure are not the most clear - would refactor if have time // may cause problems in the future
const calculateTotalExposure = (
  supplierExposure: ISupplierExposure[],
  clientExposure: Record<string, IClientExposure>,
): {
  totalExposure: {
    materialId: string;
    exposureCalendar: MonthYearValue[];
  }[];
  monthExposure: Record<string, MonthYearValue[]>;
} => {
  const totalExposuresSum = calcSupplierMinusClient(supplierExposure, clientExposure);
  const accumulatedExposures = accumulateExposures(totalExposuresSum);

  //  console.log('accumulatedExposures', accumulatedExposures, 'totalExposuresSum', totalExposuresSum);
  return {
    totalExposure: Object.entries(accumulatedExposures).map(([materialId, exposureCalendar]) => ({
      materialId,
      exposureCalendar,
    })),
    monthExposure: totalExposuresSum,
  };
};

export const onFutureOrderCreated = async (accountId: string, materialIds: string[]): Promise<void> => {
  await Promise.all([
    triggerExposureCalc({ accountId, materialIds }),
    ...materialIds.map((materialId) =>
      addNotification({
        id: `${materialId} out of balance` + Timestamp.now().seconds,
        accountId,
        date: Timestamp.now(),
        type: 'alert',
        text: `${materialId} out of balance`,
        descriptionText: `${materialId} needs to be balanced due to going out of balance`,
        actionText: 'Balance now',
        actionUrl: 'balance',
        materialId: [materialId],
        status: 'completed',
      }),
    ),
  ]);
};

export const onModifyOrder = async (
  futureOrder: IFutureOrder,
  navigate: NavigateFunction,
  updateFutureOrder: (_futureOrder: IFutureOrder) => void,
  accountId: string,
) =>
  futureOrder.status !== 'completed'
    ? goToFormStart(navigate, futureOrder.uuid)
    : modifyCompletedOrder(futureOrder, accountId, updateFutureOrder, navigate);

export const modifyCompletedOrder = async (
  futureOrder: IFutureOrder,
  accountId: string,
  updateFutureOrder: (_futureOrder: IFutureOrder) => void,
  navigate: NavigateFunction,
) => {
  const orderUUIDModified = futureOrder.uuid + MODIFIED_ORDER_SUFFIX;
  const newEditedModifiedFutureOrder: IFutureOrder = {
    ...futureOrder,
    uuid: orderUUIDModified,
    status: 'modified',
  };
  const createdFutureOrderStatus = await createFutureOrder(accountId, newEditedModifiedFutureOrder);
  if (createdFutureOrderStatus && createdFutureOrderStatus.status === 'success') {
    updateFutureOrder(newEditedModifiedFutureOrder);
    goToFormStart(navigate, orderUUIDModified);
  }
};
